import { Component, computed, effect, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CurrencyInputFieldComponent } from '@zayan-erp/shared';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { SellingPriceHistory, SellingPriceHistoryService } from '../../services/selling-price-history.service';
@Component({
  selector: 'lib-selling-price-history-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CurrencyInputFieldComponent, DatePickerModule],
  templateUrl: './selling-price-history-form.component.html'
})
export class SellingPriceHistoryFormComponent {
  #sellingPriceHistoryService = inject(SellingPriceHistoryService);
  editingSellingPriceHistory = model<SellingPriceHistory | null>(null);
  newFieldDetails = output<SellingPriceHistory>();
  addNew = model<boolean>(false);
  currencySymbol = input<string | null>(null);
  reload = output<void>();
  modelElevationProductId = input<string>();
  sellingPriceHistoryForm = new FormGroup({
    modelElevationProductId: new FormControl<string | null>(null, [Validators.required]),
    effectiveDate: new FormControl<Date | null>(null, [Validators.required]),
    quantity: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
    unitPrice: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
    retailCredit: new FormControl<number>(0, [Validators.required, Validators.min(0)])
  });
  quantity = toSignal(this.sellingPriceHistoryForm.get('quantity')!.valueChanges, {
    initialValue: this.sellingPriceHistoryForm.get('quantity')!.value
  });
  unitPrice = toSignal(this.sellingPriceHistoryForm.get('unitPrice')!.valueChanges, {
    initialValue: this.sellingPriceHistoryForm.get('unitPrice')!.value
  });
  totalAmount = computed(() => {
    return (this.quantity() ?? 0) * (this.unitPrice() ?? 0);
  });

  // Convert form valueChanges to a signal
  formValues = toSignal(this.sellingPriceHistoryForm.valueChanges, {
    initialValue: this.sellingPriceHistoryForm.value
  });

  constructor() {
    effect(() => {
      if (this.editingSellingPriceHistory()) {
        this.sellingPriceHistoryForm.patchValue(
          {
            modelElevationProductId: this.editingSellingPriceHistory()?.modelElevationProductId,
            effectiveDate: this.editingSellingPriceHistory()?.effectiveDate
              ? new Date(this.editingSellingPriceHistory()!.effectiveDate)
              : null,
            quantity: this.editingSellingPriceHistory()?.quantity,
            unitPrice: this.editingSellingPriceHistory()?.unitPrice,
            retailCredit: this.editingSellingPriceHistory()?.retailCredit
          },
          { emitEvent: true }
        );
      } else {
        this.sellingPriceHistoryForm.reset();
      }
    });

    // Emit form values when editingSellingPriceHistory is null and form values change
    effect(() => {
      if (!this.editingSellingPriceHistory() && this.formValues()) {
        this.newFieldDetails.emit(this.formValues() as SellingPriceHistory);
      }
    });
  }

  submit(): void {
    this.sellingPriceHistoryForm.patchValue({
      modelElevationProductId: this.modelElevationProductId()
    });
    if (this.sellingPriceHistoryForm.invalid) {
      return;
    }

    if (this.editingSellingPriceHistory()) {
      this.#sellingPriceHistoryService
        .updateSellingPriceHistory(this.sellingPriceHistoryForm.value as SellingPriceHistory)
        .subscribe({
          next: () => this.resetForm()
        });
    } else {
      this.#sellingPriceHistoryService
        .createSellingPriceHistory(this.sellingPriceHistoryForm.value as SellingPriceHistory)
        .subscribe({
          next: () => this.resetForm()
        });
    }
  }

  resetForm(): void {
    this.editingSellingPriceHistory.set(null);
    this.sellingPriceHistoryForm.reset();
    this.addNew.set(false);
    this.reload.emit();
  }

  onCancel(): void {
    this.resetForm();
  }
}
